import { InfoIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { LinkText } from '../../components/ui/link-text.tsx';
import {
    Toast,
    ToastClose,
    ToastDescription,
    ToastProvider,
    ToastTitle,
    ToastViewport,
} from '../../components/ui/toast.tsx';
import { useLocale } from '../../lib/hooks/use-locale.ts';
import { useVersionWatch } from './use-version-watch.ts';

/**
 * Backstop "new version available — refresh" prompt for {@link useVersionWatch}
 * (stale tabs normally reload silently on navigation).
 */
export function VersionUpdateToast(): ReactNode {
    const { t } = useLocale();
    const { showRefreshToast, dismiss } = useVersionWatch();

    return (
        <ToastProvider duration={Number.POSITIVE_INFINITY} swipeDirection="right">
            <Toast
                open={showRefreshToast}
                onOpenChange={(open) => {
                    if (!open) {
                        dismiss();
                    }
                }}
            >
                <div className="flex flex-row items-start gap-4">
                    <div className="flex grow-0">
                        <InfoIcon className="h-4 w-4 text-blue-600" />
                    </div>
                    <div className="flex flex-col gap-1">
                        <ToastTitle>{t('version_update__title')}</ToastTitle>
                        <ToastDescription>
                            {t('version_update__description')}{' '}
                            <LinkText
                                as="button"
                                onClick={() => {
                                    window.location.reload();
                                }}
                            >
                                {t('version_update__refresh_cta')}
                            </LinkText>
                        </ToastDescription>
                    </div>
                </div>
                <ToastClose />
            </Toast>
            {/* Override the shared viewport's bottom-right anchor → pin top-right. */}
            <ToastViewport className="bottom-auto top-0 sm:bottom-auto sm:top-0" />
        </ToastProvider>
    );
}
